import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { CreditCard, RefreshCw, ChevronLeft, ChevronRight, Crown, Sparkles } from 'lucide-react';
import PartnerLayout from '../../components/PartnerLayout';
import { partnerPortalAPI } from '../../lib/partnerApi';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const EventBadge = ({ type }) => {
  const colors = {
    purchase: 'bg-green-100 text-green-700',
    initial_buy: 'bg-green-100 text-green-700',
    subscribe: 'bg-green-100 text-green-700',
    renewal: 'bg-blue-100 text-blue-700',
    did_renew: 'bg-blue-100 text-blue-700',
    cancel: 'bg-red-100 text-red-700',
    cancelled: 'bg-red-100 text-red-700',
    expired: 'bg-muted text-gray-600',
    admin_extend: 'bg-purple-100 text-purple-700',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium capitalize', colors[type] || 'bg-muted text-gray-600')}>
      {(type || '—').replace(/_/g, ' ')}
    </span>
  );
};

const PlanBadge = ({ type }) => {
  if (type === 'premium') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"><Crown className="w-3 h-3" />Premium</span>;
  if (type === 'ai_plan') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><Sparkles className="w-3 h-3" />AI Plan</span>;
  return <span className="text-xs text-muted-foreground">—</span>;
};

const PartnerPaymentHistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnerPortalAPI.paymentHistory({ page: pagination.page, limit: pagination.limit });
      setHistory(res.data.history || []);
      setPagination(p => ({ ...p, total: res.data.total, pages: res.data.pages }));
    } catch { toast.error('Failed to load payment history'); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <PartnerLayout>
      <div data-testid="partner-payment-history" className="space-y-6 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Customer Payment History</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Subscription events for your referred customers</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>

        <div className="bg-card rounded-xl border border-border p-4 shadow-sm text-center max-w-xs">
          <p className="text-2xl font-bold">{pagination.total}</p>
          <p className="text-xs text-muted-foreground mt-1">Total Payment Events</p>
        </div>

        <div data-testid="payment-history-table" className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  {['Date', 'Customer', 'Plan', 'Event', 'Platform'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No payment history found</td></tr>
                ) : (
                  history.map((h, i) => (
                    <tr key={h._id || i} className="border-t border-border hover:bg-muted">
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(h.createdAt)}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-sm">{h.customer_name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{h.customer_email}</p>
                      </td>
                      <td className="px-4 py-3"><PlanBadge type={h.subscription_type} /></td>
                      <td className="px-4 py-3"><EventBadge type={h.event_type} /></td>
                      <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{h.platform || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm">Page {pagination.page} of {pagination.pages}</span>
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PartnerLayout>
  );
};

export default PartnerPaymentHistoryPage;
